import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Stack, CircularProgress, Chip,
  List, ListItemButton, ListItem, ListItemText, Divider
} from '@mui/material';
import { Add, ArrowBack, ChevronRight } from '@mui/icons-material';
import { settlementsApi, sharedExpensesApi } from '../../services/api';
import { useNotification } from '../../hooks/useNotification';
import CreateExpenseDialog from '../CreateExpenseDialog';
import ExpenseList from './ExpenseList';
import SettleUpDialog from './SettleUpDialog';
import { formatMoney } from '../../utils/money';

const formatAmount = (value) => formatMoney(Math.abs(Number(value)));
const personName = (person) => (person.isYou ? 'You' : person.name || person.email);

// Balance text from my point of view: positive = they owe me
function BalanceText({ balance, name, variant = 'body2' }) {
  if (balance > 0) {
    return <Typography variant={variant} color="success.main">{name} owes you {formatAmount(balance)}</Typography>;
  }
  if (balance < 0) {
    return <Typography variant={variant} color="error.main">You owe {name} {formatAmount(balance)}</Typography>;
  }
  return <Typography variant={variant} color="text.secondary">Settled up</Typography>;
}

// One person: overall balance, balance per group, one-to-one expenses and payments
function FriendDetail({ email, onBack, onOpenGroup }) {
  const [details, setDetails] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settleOpen, setSettleOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const { showError, showSuccess } = useNotification();

  const load = useCallback(async () => {
    try {
      const [detailsRes, expensesRes] = await Promise.all([
        settlementsApi.getFriend(email),
        sharedExpensesApi.getAll({ filter: 'friend', friendEmail: email })
      ]);
      setDetails(detailsRes.data);
      setExpenses(expensesRes.data.expenses || []);
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to load friend details');
    } finally {
      setLoading(false);
    }
  }, [email, showError]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const addDefaults = useMemo(() => (details ? {
    splitType: 'friends',
    selectedFriends: [{ id: details.friend.friendId || details.friend.email, email: details.friend.email, name: details.friend.name }]
  } : null), [details]);

  const settleTarget = useMemo(() => (details
    ? { ...details.friend, balance: details.balance, breakdown: details.breakdown }
    : null), [details]);

  const handleCreate = async (expenseData) => {
    await sharedExpensesApi.create(expenseData);
    showSuccess('Expense created');
    await load();
  };

  if (loading || !details) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  const name = details.friend.name || details.friend.email;
  const groupBalances = details.breakdown.filter(b => b.groupId);
  const individualBalance = details.breakdown.find(b => !b.groupId);

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={onBack} sx={{ mb: 2 }}>
        All friends and groups
      </Button>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2}>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h5">{name}</Typography>
                {!details.friend.isOnPlatform && <Chip label="Not on platform" size="small" variant="outlined" />}
              </Stack>
              {details.friend.name && (
                <Typography variant="body2" color="text.secondary">{details.friend.email}</Typography>
              )}
              <Box mt={1}>
                <BalanceText balance={details.balance} name={name} variant="h6" />
              </Box>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" startIcon={<Add />} onClick={() => setAddOpen(true)}>
                Add expense
              </Button>
              {details.balance !== 0 && (
                <Button variant="contained" onClick={() => setSettleOpen(true)}>
                  Settle up
                </Button>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {details.breakdown.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" mb={1}>Balances</Typography>
            <List disablePadding>
              {individualBalance && (
                <ListItem disableGutters>
                  <ListItemText primary="Individual expenses" secondary="Split between you outside any group" />
                  <BalanceText balance={individualBalance.balance} name={name} />
                </ListItem>
              )}
              {groupBalances.map((group, index) => (
                <React.Fragment key={group.groupId}>
                  {(index > 0 || individualBalance) && <Divider component="li" />}
                  <ListItemButton disableGutters onClick={() => onOpenGroup(group.groupId)} sx={{ px: 0 }}>
                    <ListItemText primary={group.groupName} secondary="Group" />
                    <BalanceText balance={group.balance} name={name} />
                    <ChevronRight color="action" sx={{ ml: 1 }} />
                  </ListItemButton>
                </React.Fragment>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      <Typography variant="h6" mb={2}>Individual expenses</Typography>
      <ExpenseList
        expenses={expenses}
        focusEmail={details.friend.email}
        showGroup={false}
        emptyText={`No individual expenses with ${name} yet.`}
        onChanged={load}
      />

      {details.payments.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" mb={1}>Payments</Typography>
            <List disablePadding>
              {details.payments.map((payment, index) => (
                <React.Fragment key={payment.id}>
                  {index > 0 && <Divider component="li" />}
                  <ListItem disableGutters>
                    <ListItemText
                      primary={`${personName(payment.from)} paid ${personName(payment.to)}`}
                      secondary={`${new Date(payment.settledAt).toLocaleDateString()}${payment.group ? ` · ${payment.group.name}` : ''}`}
                    />
                    <Typography fontWeight="bold" color={payment.direction === 'received' ? 'success.main' : 'text.primary'}>
                      {formatAmount(payment.amount)}
                    </Typography>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      <SettleUpDialog
        open={settleOpen}
        target={settleTarget}
        onClose={() => setSettleOpen(false)}
        onSettled={load}
      />

      <CreateExpenseDialog
        open={addOpen}
        defaults={addDefaults}
        onClose={() => setAddOpen(false)}
        onSuccess={handleCreate}
      />
    </Box>
  );
}

export default FriendDetail;
